import { ArrowType } from '@ardoq/api-types';
import { colors } from '@ardoq/design-tokens';
import type { Point } from '@ardoq/math';
import { CanvasRenderingContext } from './types';

/**
 * How a sub-shape is painted beneath its stroke. `hollow` fills it with the canvas
 * background, so the reference line does not show through a ring or a diamond.
 */
export type MarkerFill = 'none' | 'solid' | 'hollow';

/** One figure of a marker: an open run of segments, a closed one, or a circle. */
export type MarkerFigure =
  | { kind: 'polyline'; points: Point[] }
  | { kind: 'polygon'; points: Point[] }
  | { kind: 'circle'; center: Point; radius: number };

/** One sub-shape of a marker: the figures it is drawn from, and how they are painted. */
export type MarkerShape = {
  fill: MarkerFill;
  figures: MarkerFigure[];
};

/* Markers are black and white wherever they are drawn — the view legend, the metamodel
   editor and Block Diagram have always agreed on that, and taking the reference's own
   colour was what set the other renderers adrift. */
const MARKER_STROKE = colors.black950;
const MARKER_HOLLOW_FILL = colors.white950;

const polyline = (...points: Point[]): MarkerFigure => ({
  kind: 'polyline',
  points,
});

const polygon = (...points: Point[]): MarkerFigure => ({
  kind: 'polygon',
  points,
});

const circle = (center: Point, radius: number): MarkerFigure => ({
  kind: 'circle',
  center,
  radius,
});

const stroked = (...figures: MarkerFigure[]): MarkerShape => ({
  fill: 'none',
  figures,
});

const solid = (...figures: MarkerFigure[]): MarkerShape => ({
  fill: 'solid',
  figures,
});

const hollow = (...figures: MarkerFigure[]): MarkerShape => ({
  fill: 'hollow',
  figures,
});

/**
 * The sub-shapes of one line ending at unit size, anchored at the origin and pointing
 * along +x. {@link traceMarkerShapes} and {@link paintMarkerShapes} scale, turn and
 * place it; nothing here knows where the marker goes.
 *
 * Unit-sized because every coordinate below is `size` times a constant — the circle's
 * radius included — so one template serves every size exactly. That is what makes it
 * cacheable: marker size is continuous with zoom in two of the three renderers
 * (`clamp(SIZE / _pixel, …)` in aq-blocks, `5 * viewTransform.k` in relationships), so a
 * cache keyed on size would hold a fresh entry for every zoom step.
 *
 * Crow's-foot cardinality follows standard notation: the symbol nearest the component
 * carries the maximum (a bar for one, a foot for many), and the optionality ring sits
 * outside it.
 *
 * Returns no shapes for `ArrowType.NONE` or an unrecognised type; callers that want to
 * report the latter can test for an empty result.
 */
const unitMarkerShapes = (arrowType: ArrowType): MarkerShape[] => {
  const trianglePoints: Point[] = [
    [-1, -1],
    [0, 0],
    [-1, 1],
  ];

  const diamondPoints: Point[] = [
    [-2, -1],
    [0, 0],
    [-2, 1],
    [-4, 0],
  ];

  const bar = (offset: number): MarkerFigure =>
    polyline([-offset, -1], [-offset, 1]);

  /** Three prongs from a common apex, the middle one running along the reference. */
  const crowsFoot: MarkerFigure[] = [
    polyline([-1, 0], [0, -1]),
    polyline([-1, 0], [0, 0]),
    polyline([-1, 0], [0, 1]),
  ];

  const ring = (offset: number): MarkerFigure => circle([-offset, 0], 1);

  /* Clear of the cardinality symbol, which ends at 1. */
  const optionalityRing = ring(2);

  switch (arrowType) {
    case ArrowType.CIRCLE:
    case ArrowType.CIRCLE_START:
      return [solid(ring(0.5))];

    case ArrowType.BOTH:
    case ArrowType.BOTH_START:
      return [stroked(polyline(...trianglePoints))];

    case ArrowType.BOTH_FILLED:
    case ArrowType.BOTH_FILLED_START:
      return [solid(polygon(...trianglePoints))];

    case ArrowType.BOTH_OUTLINED:
    case ArrowType.BOTH_OUTLINED_START:
      return [hollow(polygon(...trianglePoints))];

    case ArrowType.DIAMOND:
    case ArrowType.DIAMOND_START:
      return [hollow(polygon(...diamondPoints))];

    case ArrowType.DIAMOND_FILLED:
    case ArrowType.DIAMOND_FILLED_START:
      return [solid(polygon(...diamondPoints))];

    case ArrowType.TOP:
    case ArrowType.BOTTOM_START:
      return [stroked(polyline([-1, -1], [0, 0]))];

    case ArrowType.TOP_START:
    case ArrowType.BOTTOM:
      return [stroked(polyline([-1, 1], [0, 0]))];

    case ArrowType.ONE:
    case ArrowType.ONE_START:
      return [stroked(bar(1))];

    case ArrowType.ONE_MANDATORY:
    case ArrowType.ONE_MANDATORY_START:
      return [stroked(bar(1), bar(2))];

    case ArrowType.MANY:
    case ArrowType.MANY_START:
      return [stroked(...crowsFoot)];

    case ArrowType.ONE_OR_MANY:
    case ArrowType.ONE_OR_MANY_START:
      return [stroked(...crowsFoot, bar(1))];

    case ArrowType.ZERO_OR_ONE:
    case ArrowType.ZERO_OR_ONE_START:
      return [
        hollow(optionalityRing),
        stroked(polyline([-1, 0], [0, 0]), bar(0.5)),
      ];

    case ArrowType.ZERO_OR_MANY:
    case ArrowType.ZERO_OR_MANY_START:
      return [hollow(optionalityRing), stroked(...crowsFoot)];

    case ArrowType.DEFAULT:
    case ArrowType.DEFAULT_START:
      return [stroked(polyline([0, -1], [-1, 1]))];

    default:
      return [];
  }
};

/**
 * Bounded by the enum, so it cannot grow with use: one entry per arrow type ever drawn,
 * built on first sight and kept. Callers must treat what they get back as read-only —
 * every renderer shares the one copy.
 */
const templates = new Map<ArrowType, MarkerShape[]>();

/** The unit-sized, origin-anchored geometry for one line ending. */
export const markerShapes = (arrowType: ArrowType): MarkerShape[] => {
  const cached = templates.get(arrowType);
  if (cached) return cached;

  const shapes = unitMarkerShapes(arrowType);
  templates.set(arrowType, shapes);
  return shapes;
};

/** Where a marker goes: its anchor, the direction it faces, and how big it is drawn. */
export type MarkerPlacement = {
  anchor: Point;
  /** Radians. 0 leaves the template's own +x direction, which skips the turn entirely. */
  angle: number;
  size: number;
};

/**
 * Scales a template offset and puts it where the placement says, without building a point.
 *
 * This replaces a `rotateMarkerShapes` pass that mapped every shape, every figure and
 * every point into fresh objects before anything was drawn. Markers are traced two to
 * four times per link per frame inside two canvas loops, and aq-blocks' scenegraph keeps
 * requesting frames while a layout settles, so that pass ran at animation rate.
 */
type Placed = {
  anchorX: number;
  anchorY: number;
  size: number;
  cos: number;
  sin: number;
};

const toPlaced = ({
  anchor: [x, y],
  angle,
  size,
}: MarkerPlacement): Placed => ({
  anchorX: x,
  anchorY: y,
  size,
  cos: Math.cos(angle),
  sin: Math.sin(angle),
});

const placedX = ([dx, dy]: Point, p: Placed): number =>
  p.anchorX + p.size * (dx * p.cos - dy * p.sin);

const placedY = ([dx, dy]: Point, p: Placed): number =>
  p.anchorY + p.size * (dx * p.sin + dy * p.cos);

const traceFigure = (
  ctx: CanvasRenderingContext,
  figure: MarkerFigure,
  placed: Placed
): void => {
  if (figure.kind === 'circle') {
    ctx.ellipse(
      placedX(figure.center, placed),
      placedY(figure.center, placed),
      figure.radius * placed.size,
      figure.radius * placed.size,
      0,
      0,
      2 * Math.PI
    );
    return;
  }

  /* Indexed rather than destructured: `const [first, ...rest]` allocates a copy of the
     tail on every figure, which is the allocation this rewrite exists to remove. */
  const { points } = figure;
  ctx.moveTo(placedX(points[0], placed), placedY(points[0], placed));
  for (let at = 1; at < points.length; at++) {
    ctx.lineTo(placedX(points[at], placed), placedY(points[at], placed));
  }
  if (figure.kind === 'polygon') ctx.closePath();
};

/** Adds every figure to the current path, for callers that stroke the marker as one. */
export const traceMarkerShapes = (
  ctx: CanvasRenderingContext,
  shapes: MarkerShape[],
  placement: MarkerPlacement
): void => {
  const placed = toPlaced(placement);
  for (const { figures } of shapes) {
    for (const figure of figures) traceFigure(ctx, figure, placed);
  }
};

/**
 * Fills and strokes each sub-shape in turn, so a ring drawn under a foot can occlude
 * the reference while the foot on top stays open.
 */
export const paintMarkerShapes = (
  ctx: CanvasRenderingContext,
  shapes: MarkerShape[],
  placement: MarkerPlacement,
  strokeStyle?: string,
  fillStyle?: string
): void => {
  const placed = toPlaced(placement);
  ctx.strokeStyle = strokeStyle ?? MARKER_STROKE;

  for (const { fill, figures } of shapes) {
    ctx.beginPath();
    for (const figure of figures) traceFigure(ctx, figure, placed);

    if (fill !== 'none') {
      ctx.fillStyle = ctx.fillStyle =
        fill === 'solid' ? (fillStyle ?? MARKER_STROKE) : MARKER_HOLLOW_FILL;
      ctx.fill();
    }

    ctx.stroke();
  }
};
